import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { MATERIAUX } from 'app/classesTs/Materiax';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import Swal from 'sweetalert2';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-listemateriaux',
  templateUrl: './listemateriaux.component.html',
  styleUrls: ['./listemateriaux.component.scss']
})
export class ListemateriauxComponent implements OnInit, OnDestroy {
  // Material Data (Fournisseurs)
  allMaterials: any[] = [];
  filteredMaterials: any[] = [];
  
  // Filtering & Search
  selectedType: string = 'all';
  searchTerm: string = '';
  materiaux = MATERIAUX;
  
  // Pagination with URL persistence
  currentPage: number = 1;
  itemsPerPage: number = 7;
  totalItems: number = 0;
  
  // Loading State
  isLoading: boolean = false;
  errorMessage: string = '';
  
  // Router subscription
  private routerSubscription: Subscription;
  
  // Data loaded flag
  dataLoaded: boolean = false;

  constructor(
    private agenciesService: AgenciesService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) {
    // Listen to route changes to restore state
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.restoreStateFromUrl();
      });
  }

  ngOnInit(): void {
    // First restore state from URL, then load data
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      this.restoreStateFromUrl();
      this.loadMaterials();
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Restore state from URL parameters
   */
  private restoreStateFromUrl(): void {
    this.route.queryParams.subscribe(params => {
      // Restore page number (default to 1 if not provided)
      this.currentPage = params['page'] ? +params['page'] : 1;
      
      // Restore material type filter (default to 'all')
      this.selectedType = params['type'] || 'all';
      
      // Restore search term (default to empty)
      this.searchTerm = params['search'] || '';
    });
  }

  /**
   * Update URL with current state
   */
  private updateUrlState(): void {
    const queryParams: any = {};
    
    // Always include page if not 1
    if (this.currentPage !== 1) {
      queryParams['page'] = this.currentPage;
    } else {
      queryParams['page'] = null;
    }
    
    // Always include type if not 'all'
    if (this.selectedType !== 'all') {
      queryParams['type'] = this.selectedType;
    } else {
      queryParams['type'] = null;
    }
    
    // Include search term if not empty
    if (this.searchTerm.trim()) {
      queryParams['search'] = this.searchTerm;
    } else {
      queryParams['search'] = null;
    }

    // Create URL tree with query params
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge'
    });

    // Update URL without triggering navigation
    this.location.replaceState(urlTree.toString());
  }

  /**
   * Load all materials from API - FIXED: Use getListMateriaux() instead of getListAgenceImmob()
   */
  loadMaterials(): void {
    this.isLoading = true;
    this.errorMessage = '';
    
    // Use getListMateriaux() which should return ALL materials
    this.agenciesService.getListMateriaux().subscribe({
      next: (response: any) => {
        console.log("All materials API response:", response);
        this.handleApiResponse(response);
      },
      error: (error) => {
        console.error("Error loading all materials:", error);
        // Fallback: try the general endpoint if specific one fails
        this.agenciesService.getListAgenceImmob().subscribe({
          next: (response: any) => {
            this.handleApiResponse(response);
          },
          error: (err) => {
            this.handleApiError(err);
          }
        });
      }
    });
  }

  /**
   * Load materials by type
   */
  loadMaterialsByType(typeId: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.selectedType = typeId;
    this.currentPage = 1;
    
    // Update URL immediately when filter changes
    this.updateUrlState();
    
    if (typeId === 'all') {
      this.loadMaterials();
    } else {
      // Call specific API endpoint for material type
      switch(typeId) {
        case 'equipement':
          this.agenciesService.getMateriauxEquipement().subscribe({
            next: (response: any) => {
              this.handleApiResponse(response);
            },
            error: (error) => {
              this.handleApiError(error);
            }
          });
          break;
        case 'automatisme':
          this.agenciesService.getMateriauxAutomatisme().subscribe({
            next: (response: any) => {
              this.handleApiResponse(response);
            },
            error: (error) => {
              this.handleApiError(error);
            }
          });
          break;
        case 'chauffage':
          this.agenciesService.getMateriauxChauffage().subscribe({
            next: (response: any) => {
              this.handleApiResponse(response);
            },
            error: (error) => {
              this.handleApiError(error);
            }
          });
          break;
        case 'energie':
          this.agenciesService.getMateriauxEnergie().subscribe({
            next: (response: any) => {
              this.handleApiResponse(response);
            },
            error: (error) => {
              this.handleApiError(error);
            }
          });
          break;
        case 'meuble':
          this.agenciesService.getMateriauxMeuble().subscribe({
            next: (response: any) => {
              this.handleApiResponse(response);
            },
            error: (error) => {
              this.handleApiError(error);
            }
          });
          break;
        case 'protection':
          this.agenciesService.getMateriauxProtection().subscribe({
            next: (response: any) => {
              this.handleApiResponse(response);
            },
            error: (error) => {
              this.handleApiError(error);
            }
          });
          break;
        case 'menuiserie':
          this.agenciesService.getMateriauxMenuiserie().subscribe({
            next: (response: any) => {
              this.handleApiResponse(response);
            },
            error: (error) => {
              this.handleApiError(error);
            }
          });
          break;
        case 'decoration':
          this.agenciesService.getMateriauxDecoration().subscribe({
            next: (response: any) => {
              this.handleApiResponse(response);
            },
            error: (error) => {
              this.handleApiError(error);
            }
          });
          break;
        default:
          this.loadMaterials();
      }
    }
  }

  /**
   * Handle API response - SIMPLIFIED VERSION
   */
  private handleApiResponse(response: any): void {
    try {
      console.log("Processing API response...");
      
      let materialsData: any[] = [];
      
      // The API returns an array directly
      if (Array.isArray(response)) {
        materialsData = response;
        console.log("Data is an array, length:", materialsData.length);
        
        // Log first item to see structure
        if (materialsData.length > 0) {
          console.log("First item structure:", {
            id: materialsData[0].id,
            name: materialsData[0].name,
            role: materialsData[0].role,
            categorie: materialsData[0].categorie,
            produits: materialsData[0].produits?.length || 0
          });
        }
      } 
      // If it's wrapped in some property
      else if (response && typeof response === 'object') {
        console.log("Response is an object, keys:", Object.keys(response));
        
        // Check for common response structures
        if (response.data && Array.isArray(response.data)) {
          materialsData = response.data;
          console.log("Found data in response.data, length:", materialsData.length);
        } else {
          // Try to find any array in the object
          const arrayValues = Object.values(response).filter(val => Array.isArray(val));
          if (arrayValues.length > 0) {
            materialsData = arrayValues[0] as any[];
            console.log("Found array in object values, length:", materialsData.length);
          }
        }
      }
      
      console.log("Total items received:", materialsData.length);
      
      // For "Tous", we need to collect ALL materials from all types
      // Since each endpoint returns only specific type, we need to handle "all" differently
      if (this.selectedType === 'all') {
        // When "Tous" is selected, we need to combine data from all material types
        // For now, just use whatever data we got
        this.allMaterials = materialsData;
      } else {
        // For specific types, just use the data from that endpoint
        this.allMaterials = materialsData;
      }
      
      console.log("Materials loaded:", this.allMaterials.length);
      
      // Sort materials: those with produits first
      this.allMaterials.sort((a: any, b: any) => {
        const aProduits = Array.isArray(a.produits) ? a.produits.length : 0;
        const bProduits = Array.isArray(b.produits) ? b.produits.length : 0;
        
        if (aProduits === 0 && bProduits > 0) {
          return 1; // Move agencie with empty produits to the end
        } else if (aProduits > 0 && bProduits === 0) {
          return -1; // Move agencie with non-empty produits to the beginning
        } else {
          return 0; // No change in order
        }
      });
      
      this.applyFilters();
      
      this.isLoading = false;
      this.dataLoaded = true;
    } catch (error) {
      console.error('Error processing response:', error);
      this.errorMessage = 'Erreur lors du traitement des données';
      this.allMaterials = [];
      this.applyFilters();
      this.isLoading = false;
      this.dataLoaded = true;
    }
  }

  /**
   * Handle API error
   */
  private handleApiError(error: any): void {
    console.error('Error loading materials:', error);
    this.errorMessage = 'Erreur lors du chargement des matériaux';
    this.isLoading = false;
    this.dataLoaded = true;
    this.allMaterials = [];
    this.applyFilters();
  }

  /**
   * Get count for a specific material type - FIXED
   */
  getTypeCount(typeId: string): number {
    if (typeId === 'all') return this.allMaterials.length;
    
    return this.allMaterials.filter(material => {
      const materialCategorie = material.categorie || '';
      return materialCategorie.toLowerCase() === typeId.toLowerCase();
    }).length;
  }

  /**
   * Get total produits count
   */
  getTotalProduits(): number {
    return this.allMaterials.reduce((total, material) => {
      return total + (Array.isArray(material.produits) ? material.produits.length : 0);
    }, 0);
  }

  /**
   * Apply filters (search only)
   */
  applyFilters(): void {
    let filtered = [...this.allMaterials];
    
    // Filter by search term
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(material => {
        const nameMatch = material.name?.toLowerCase().includes(searchLower) || false;
        const addressMatch = material.adresse?.toLowerCase().includes(searchLower) || false;
        const emailMatch = material.email?.toLowerCase().includes(searchLower) || false;
        const phoneMatch = material.phone_number?.toString().includes(searchLower) || false;
        const roleMatch = material.role?.toLowerCase().includes(searchLower) || false;
        const categorieMatch = material.categorie?.toLowerCase().includes(searchLower) || false;
        return nameMatch || addressMatch || emailMatch || phoneMatch || roleMatch || categorieMatch;
      });
    }
    
    this.filteredMaterials = filtered;
    this.totalItems = filtered.length;
    
    // Ensure current page is valid
    const maxPage = Math.ceil(this.totalItems / this.itemsPerPage);
    if (this.currentPage > maxPage && maxPage > 0) {
      this.currentPage = maxPage;
      this.updateUrlState();
    }
    
    console.log("Filtered materials:", this.filteredMaterials.length, "Total items:", this.totalItems);
  }

  /**
   * Perform search
   */
  performSearch(): void {
    this.currentPage = 1;
    this.applyFilters();
    this.updateUrlState();
  }

  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    this.selectedType = 'all';
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadMaterials();
    this.updateUrlState();
  }

  /**
   * Get type label from categorie value
   */
  getTypeLabel(categorieValue: string): string {
    if (!categorieValue) return 'Non spécifié';
    
    const type = this.materiaux.find(m => m.value === categorieValue.toLowerCase());
    return type ? type.label : 
      categorieValue.charAt(0).toUpperCase() + categorieValue.slice(1);
  }

  /**
   * Handle page change
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.updateUrlState();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(size: number): void {
    this.itemsPerPage = size;
    this.currentPage = 1;
    this.applyFilters();
    this.updateUrlState();
  }

  /**
   * Get display range for pagination info
   */
  getDisplayRange(): { start: number, end: number } {
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    return { start, end };
  }

  /**
   * Handle image error
   */
  handleImageError(event: any): void {
    event.target.src = 'assets/default-material.png';
  }

  /**
   * Delete material with confirmation
   */
  deleteMaterial(material: any, index: number): void {
    if (!material.id || !material.name) return;
    
    Swal.fire({
      title: `Supprimer ${material.name}?`,
      text: "Cette action est irréversible et supprimera également tous les produits associés.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff4757",
      cancelButtonColor: "#2ed573",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
      reverseButtons: true,
      backdrop: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        this.agenciesService.deleteItem(material.id).subscribe({
          next: () => {
            Swal.fire({
              title: "Supprimé!",
              text: "Le fournisseur a été supprimé avec succès.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false
            });
            
            // Remove from arrays
            const globalIndex = this.allMaterials.findIndex(m => m.id === material.id);
            if (globalIndex !== -1) {
              this.allMaterials.splice(globalIndex, 1);
            }
            
            const filteredIndex = this.filteredMaterials.findIndex(m => m.id === material.id);
            if (filteredIndex !== -1) {
              this.filteredMaterials.splice(filteredIndex, 1);
            }
            
            this.applyFilters();
          },
          error: (error) => {
            console.error('Error deleting material:', error);
            Swal.fire({
              title: "Erreur!",
              text: "Impossible de supprimer le fournisseur. Veuillez réessayer.",
              icon: "error"
            });
          }
        });
      }
    });
  }

  /**
   * Get status color based on active status
   */
  getStatusColor(active: boolean): string {
    return active ? '#2ecc71' : '#e74c3c';
  }

  /**
   * Get status label
   */
  getStatusLabel(active: boolean): string {
    return active ? 'Actif' : 'Inactif';
  }
}